'use client';

import { trackThemePreference } from '@workspace/analytics/posthog/events';
import { cn } from '@workspace/ui/lib';
import { useThemeAnalyticsApp } from '@workspace/ui/providers';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useState } from 'react';

// Define theme labels as constants
const THEME_LABELS = {
  light: 'Light mode',
  dark: 'Dark mode',
} as const;

interface ThemeToggleProps {
  className?: string;
  iconClassName?: string;
  labelClassName?: string;
}

export function ThemeToggle({ className, iconClassName, labelClassName }: ThemeToggleProps) {
  const analyticsApp = useThemeAnalyticsApp();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleThemeToggle = useCallback(() => {
    const currentTheme = resolvedTheme ?? theme;
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';

    // The provider's ThemeCookieSync effect mirrors this into the cross-subdomain cookie.
    setTheme(newTheme);

    if (analyticsApp) {
      trackThemePreference({
        app: analyticsApp,
        theme: newTheme,
        source: 'user_toggle',
      });
    }
  }, [analyticsApp, theme, resolvedTheme, setTheme]);

  if (!mounted) {
    return null;
  }

  return (
    <button
      className={cn(
        'h-9 group peer/menu-button text-sidebar-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex w-full cursor-pointer items-center gap-2 overflow-hidden rounded-md px-3 py-2 text-left text-sm outline-hidden transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:p-2! focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-5 [&>svg]:shrink-0',
        className
      )}
      onClick={handleThemeToggle}
    >
      {(resolvedTheme ?? theme) === 'light' ? (
        <Moon className={cn('size-5 min-[1199px]:stroke-2!', iconClassName)} />
      ) : (
        <Sun className={cn('size-5 min-[1199px]:stroke-2!', iconClassName)} />
      )}
      <p className={cn('text-sidebar-foreground min-[1199px]:font-medium', labelClassName)}>
        {(resolvedTheme ?? theme) === 'light' ? THEME_LABELS.dark : THEME_LABELS.light}
      </p>
    </button>
  );
}
